/* eslint-disable no-restricted-globals */
/* eslint-disable max-len */
/* eslint-disable no-alert */
/* eslint-disable no-nested-ternary */
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { Pricings } from '@brands/services/identity/getPricings';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectOrganizationPrices } from '@brands/store/selectors/organizationPrices';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { setForm, UploadedFile } from '@brands/store/slices/patientFormSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { needsPayment } from '@brands/Utils/needsPayment';
import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Button } from '@mui/material';
import { heicTo } from 'heic-to';
import uniqBy from 'lodash/uniqBy';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IoClose } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import Container from '../../../../Components/Container/Container';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useReduxHook';
import useWindowSize from '../../../../hooks/useWindowSize';
import { truncateFilename } from '../../../../Utils/truncateString';
import AddDependentModal from '../../../MyProfile/Patient/Sections/AddDependentModal/AddDependentModal';
import othersType from './assets/others-file-icon.svg';
import Camera from './Components/Camera';
import styles from './SecondOpinion/styles.module.scss';
import { uploadIntakeImage, uploadIntakeImages } from '@brands/Utils/uploadIntakeImage';
import { clearPreviewCache } from '@brands/Utils/mediaPreviewCache';
import { UploadedFileImage } from '@brands/Components/UploadedFileImage/UploadedFileImage';
import { CASE_ATTACHMENT_ACCEPT, isAcceptedCaseAttachmentFile } from '@brands/Utils/caseAttachmentFiles';

type ImageUploadSubmit = {
  images: UploadedFile[];
};

const dataURLToFile = async (dataURL: string, filename: string): Promise<File> => {
  const response = await fetch(dataURL);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type || 'image/png' });
};

const ImageUploadSecondOpinion = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { userInfo } = useAppSelector(selectAuth);
  const { values } = useAppSelector(selectPatientForm);
  const { currentOrganizationPrices } = useAppSelector(selectOrganizationPrices);
  const screenSize = useWindowSize();
  const [openCamera, setOpenCamera] = useState(false);
  const [showAddDependentModal, setShowAddDependentModal] = useState<boolean>(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<UploadedFile[]>(
    (values.find((item) => item.form_question_id === 17)?.value.images as UploadedFile[]) || [],
  );
  const searchParams = new URLSearchParams(location.search);
  const routeCaseType = searchParams.get('case_type');
  const caseType: string = !routeCaseType
    ? (values.find((item) => item.form_question_id === 20)?.value.caseType as string)
    : routeCaseType;

  const currentOrganizationPrice = currentOrganizationPrices?.find((currentPrice: Pricings) =>
    currentPrice.case_types.includes(caseType as string),
  );

  const onSubmit = (data: ImageUploadSubmit): void => {
    const payload = [...values!];
    const filteredPayload = payload.filter((item) => item.form_question_id !== 17);

    filteredPayload.push({
      form_question_id: 17,
      value: { ...data },
    });

    dispatch(setForm(filteredPayload));

    roleNavigate(needsPayment(currentOrganizationPrice) ? '/payment-information' : '/confirm-payment');
  };

  const onInvalid = (errors: any): void => {
    const error = Object.values(errors)[0] as any;
    if (error) {
      toast.error(error.message as string);
    }
  };

  const validationSchema = Yup.object().shape({
    images:
      (values.find((item) => item.form_question_id === 20)?.value.caseType as string) === 'one_report'
        ? Yup.array()
            .min(2, 'At least 2 images are required to upload.')
            .max(10, 'Only 10 images are allowed to upload.')
            .required('At least 2 images are required to upload.')
        : (values.find((item) => item.form_question_id === 20)?.value.caseType as string) === 'second_opinion'
        ? Yup.array()
            .min(1, 'At least 1 attachment is required to upload.')
            .max(10, 'Only 10 attachments are allowed to upload.')
            .required('At least 1 attachment is required to upload.')
        : Yup.array().max(10, 'Only 10 images are allowed to upload.').nullable(),
  });

  const { setValue, register, handleSubmit } = useForm<ImageUploadSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const onCloseCamera = (): void => {
    setOpenCamera(false);
  };

  const onPhotoTaken = async (uri: string): Promise<void> => {
    try {
      setUploading(true);
      const file = await dataURLToFile(uri, 'Capture.png');
      const uploaded = await uploadIntakeImage(file, userInfo?.role?.name);
      setImages((prev) => [...prev, uploaded]);
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setUploading(false);
    }
  };

  const addImages = async (files: File[]): Promise<void> => {
    const acceptedFiles = files.filter(isAcceptedCaseAttachmentFile);
    if (images.length + acceptedFiles.length > 10) {
      toast.error(
        (values.find((item) => item.form_question_id === 20)?.value.caseType as string) === 'one_report'
          ? 'Only 1-10 files are allowed to upload.'
          : 'Max 10 files are allowed to upload.',
      );
      return;
    }
    setUploading(true);
    try {
      const successful = await uploadIntakeImages(acceptedFiles, userInfo?.role?.name);
      if (successful.length > 0) {
        setImages((prev) => uniqBy([...prev, ...successful], 'media_id'));
      }
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (event: any): void => {
    event.preventDefault();
  };

  const handleDrop = async (event: React.DragEvent<HTMLImageElement>): Promise<void> => {
    event.preventDefault();

    const files = Array.from(event.dataTransfer.files);

    const convertedFiles = await Promise.all(
      files.map(async (file) => {
        if (file.type === 'image/heic') {
          try {
            const jpegBlob = await heicTo({
              blob: file,
              type: 'image/jpeg',
              quality: 0.85,
            });

            return new File([jpegBlob], file.name.replace(/\.heic$/i, '.jpeg'), {
              type: 'image/jpeg',
            });
          } catch (error) {
            displayErrorDetails(`Error converting HEIC to JPEG: ${error}`);
            return file;
          }
        }

        return file;
      }),
    );

    void addImages(convertedFiles);
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files as unknown as File[]);

    const convertedFiles = await Promise.all(
      files.map(async (file) => {
        if (file.type === 'image/heic') {
          try {
            const jpegBlob = await heicTo({
              blob: file,
              type: 'image/jpeg',
              quality: 0.85,
            });

            return new File([jpegBlob], file.name.replace(/\.heic$/i, '.jpeg'), {
              type: 'image/jpeg',
            });
          } catch (error) {
            displayErrorDetails(`Error converting HEIC to JPEG: ${error}`);
            return file;
          }
        }

        return file;
      }),
    );

    void addImages(convertedFiles);
  };

  const deleteMedia = (index: number): void => {
    setImages((prev) => {
      const removed = prev[index];
      if (removed?.media_id) clearPreviewCache(removed.media_id);
      return prev.filter((_, i) => i !== index);
    });
  };

  useEffect(() => {
    const payload = [...values!];
    const filteredPayload = payload.filter((item) => item.form_question_id !== 17);

    if (images.length > 0) {
      setValue('images', images);

      filteredPayload.push({
        form_question_id: 17,
        value: { images },
      });
    } else {
      setValue('images', []);
      filteredPayload.push({
        form_question_id: 17,
        value: { images: [] },
      });
    }

    dispatch(setForm(filteredPayload));
  }, [images]);

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
      }}
      childrenStyle={{ zIndex: '2', paddingTop: screenSize.width > 768 ? '40px' : '16px' }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={styles.uploadImageContainer}>
        <PatientIntakeNavigation progress={68} />
        <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <div className={styles.wrapper}>
            <div className={styles.formContent}>
              <span className={styles.title}>Attachment upload</span>
              <span className={styles.content}>
                Please upload up to 10 of your recent reports, medical scans, doctors notes, and other records that will
                help the dentist complete the consultation (at least one attachment is required)
              </span>
              {images.length > 0 ? (
                <div className={styles.grid}>
                  {images.map((file, i) => {
                    const mediaId = file.media_id ?? '';
                    const isImage = (file.mimetype ?? '').startsWith('image/');
                    return (
                      <div className={styles.imageWrap} key={mediaId || file.name}>
                        <span
                          className={styles.removeImage}
                          onClick={() => deleteMedia(i)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && deleteMedia(i)}
                        >
                          <IoClose />
                        </span>
                        {isImage && mediaId ? (
                          <UploadedFileImage file={file} userRole={userInfo?.role?.name} />
                        ) : (
                          <img className={styles.othersTypeIcon} src={othersType} />
                        )}
                        <span className={styles.imageFooter}>
                          <p>{truncateFilename(file.name, 20)}</p>
                        </span>
                      </div>
                    );
                  })}
                  <label htmlFor="addMoreImagesInput" className={`${styles.imageWrap} ${styles.addMoreTile}`}>
                    <div className={styles.addMoreContent}>
                      <AddIcon className={styles.plusIcon} />
                    </div>
                    <input
                      type="file"
                      id="addMoreImagesInput"
                      hidden
                      multiple
                      onChange={handleChange}
                      accept={
                        (values.find((item) => item.form_question_id === 20)?.value.caseType as string) ===
                        'second_opinion'
                          ? CASE_ATTACHMENT_ACCEPT
                          : '.png, .jpg, .jpeg, .jfif, .pjpeg, .pjp, .heic'
                      }
                    />
                  </label>
                </div>
              ) : (
                <div className={styles.draggingArea} onDragOver={handleDragOver} onDrop={handleDrop}>
                  <p>Add an image or a file</p>
                  <div className={styles.btnWrap}>
                    <label htmlFor="formId" className={`${styles.uploadImageLabel}`}>
                      <input
                        {...register('images')}
                        name=""
                        type="file"
                        id="formId"
                        onChange={handleChange}
                        multiple
                        hidden
                        accept={
                          (values.find((item) => item.form_question_id === 20)?.value.caseType as string) ===
                          'second_opinion'
                            ? CASE_ATTACHMENT_ACCEPT
                            : '.png, .jpg, .jpeg, .jfif, .pjpeg, .pjp, .heic'
                        }
                      />

                      <div className={styles.selectImages}>
                        <UploadFileIcon />
                        <span>Select Files</span>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.btnContainer}>
              <Button variant="text" className={styles.imageUploasSOBack} onClick={() => navigate(-1)}>
                <ArrowBackIosNewIcon />
                Back
              </Button>
              <Button
                variant="contained"
                className={styles.imageUploasSONext}
                type="submit"
                disabled={
                  uploading ||
                  ((values.find((item) => item.form_question_id === 20)?.value.caseType as string) === 'one_report' &&
                    (images.length < 2 || images.length > 10)) ||
                  ((values.find((item) => item.form_question_id === 20)?.value.caseType as string) ===
                    'second_opinion' &&
                    (images.length === 0 || images.length > 10))
                }
              >
                {(values.find((item) => item.form_question_id === 20)?.value.caseType as string) === 'one_report' ||
                (values.find((item) => item.form_question_id === 20)?.value.caseType as string) === 'second_opinion'
                  ? 'Next'
                  : images.length > 0
                  ? 'Next'
                  : 'Skip'}
              </Button>
            </div>
          </div>
        </form>
        {openCamera && <Camera onClose={onCloseCamera} onPhotoTaken={onPhotoTaken} />}
      </div>
      {showAddDependentModal && (
        <AddDependentModal isOpen={showAddDependentModal} setIsOpen={setShowAddDependentModal} isFromSelectNeeds />
      )}
    </Container>
  );
};
export default ImageUploadSecondOpinion;
